import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { AdminGuard, AdminNav } from "../components/admin/AdminShell";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Plus, Certificate, WarningCircle } from "@phosphor-icons/react";

const BATCH_STATUSES = ["Draft","Awaiting Documentation","Awaiting Compliance Review","Approved","Quarantined","Expired","Recalled","Depleted","Archived"];

const NewBatchDialog = ({ products, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    product_id: "",
    internal_batch_id: "",
    public_lot_number: "",
    supplier_lot_number: "",
    supplier_name: "",
    manufacture_date: "",
    expiration_date: "",
    quantity_received: 0,
    testing_lab: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_id) { toast.error("Select a product"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/batches", { ...form, quantity_received: Number(form.quantity_received || 0) });
      toast.success("Batch created (Draft)");
      onCreated(data);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="new-batch-btn" className="inline-flex items-center gap-2 bg-brand-navy text-white hover:bg-brand-navy2 font-semibold px-4 py-2 text-xs tracking-wide">
          <Plus size={14} weight="bold" /> New batch
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white border-brand-border text-brand-navy max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display">Create batch</DialogTitle>
          <DialogDescription className="text-xs text-brand-muted font-mono">Batches start in Draft. They must be approved before their inventory becomes available.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Product</Label>
            <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
              <SelectTrigger data-testid="batch-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
              <SelectContent>
                {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.product_name} — {p.strength}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Internal batch ID</Label>
              <Input required value={form.internal_batch_id} onChange={(e) => setForm({ ...form, internal_batch_id: e.target.value })} data-testid="batch-internal-id" />
            </div>
            <div>
              <Label>Public lot number</Label>
              <Input required value={form.public_lot_number} onChange={(e) => setForm({ ...form, public_lot_number: e.target.value })} data-testid="batch-public-lot" />
            </div>
            <div>
              <Label>Supplier</Label>
              <Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} />
            </div>
            <div>
              <Label>Supplier lot #</Label>
              <Input value={form.supplier_lot_number} onChange={(e) => setForm({ ...form, supplier_lot_number: e.target.value })} />
            </div>
            <div>
              <Label>Manufacture date</Label>
              <Input type="date" value={form.manufacture_date} onChange={(e) => setForm({ ...form, manufacture_date: e.target.value })} />
            </div>
            <div>
              <Label>Expiration date</Label>
              <Input type="date" value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} />
            </div>
            <div>
              <Label>Quantity received</Label>
              <Input type="number" min={0} value={form.quantity_received} onChange={(e) => setForm({ ...form, quantity_received: e.target.value })} data-testid="batch-qty" />
            </div>
            <div>
              <Label>Testing lab</Label>
              <Input value={form.testing_lab} onChange={(e) => setForm({ ...form, testing_lab: e.target.value })} />
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="batch-create-submit" className="w-full bg-brand-navy text-white hover:bg-brand-navy2 font-semibold px-4 py-2 text-sm disabled:opacity-50">
            {busy ? "Creating…" : "Create batch"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const UploadCOA = ({ batch, onUploaded }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("batch_id", batch.id);
      fd.append("file", file);
      const token = localStorage.getItem("mps_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/coa/upload`, {
        method: "POST",
        body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
      toast.success("COA uploaded (Under Review)");
      onUploaded?.();
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest cursor-pointer ${busy ? "text-brand-muted" : "text-brand-accent hover:text-brand-accent"}`}>
      <Certificate size={12} weight="bold" /> {busy ? "Uploading…" : "Upload COA"}
      <input type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" onChange={upload} className="hidden" data-testid={`coa-upload-${batch.id.slice(0,6)}`} />
    </label>
  );
};

export default function AdminBatches() {
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);

  const load = async () => {
    const [b, p] = await Promise.all([
      api.get("/admin/batches"),
      api.get("/admin/products"),
    ]);
    setBatches(b.data);
    setProducts(p.data);
  };

  useEffect(() => { load(); }, []);

  const transition = async (id, status) => {
    try {
      await api.post(`/admin/batches/${id}/transition`, { status });
      toast.success(`Batch → ${status}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const productName = (pid) => products.find((p) => p.id === pid)?.product_name || pid.slice(0, 6);

  return (
    <AdminGuard>
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 py-10" data-testid="admin-batches-page">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="overline mb-2">Inventory</div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy">Batches & Lots</h1>
            <p className="mt-2 text-brand-muted text-sm max-w-2xl">Lot-based inventory. Only batches with status <span className="font-mono text-emerald-300">Approved</span> contribute to available inventory used by the compliance engine.</p>
          </div>
          <NewBatchDialog products={products} onCreated={load} />
        </div>

        {batches.length === 0 ? (
          <div className="border border-brand-border bg-white p-16 text-center">
            <div className="font-display text-xl text-brand-navy">No batches yet.</div>
            <p className="mt-2 text-sm text-brand-muted">Use “New batch” to create the first inventory lot for a product.</p>
          </div>
        ) : (
          <div className="border border-brand-border divide-y divide-brand-border">
            {batches.map((b) => (
              <div key={b.id} className="p-6 bg-white grid grid-cols-12 gap-4 items-start">
                <div className="col-span-3">
                  <div className="font-display text-brand-navy">{productName(b.product_id)}</div>
                  <div className="text-xs text-brand-muted font-mono">Lot {b.public_lot_number} · {b.internal_batch_id}</div>
                  <div className="mt-1 text-[10px] text-slate-600 font-mono">Supplier: {b.supplier_name || "—"}</div>
                </div>
                <div className="col-span-2 text-xs text-brand-muted font-mono">
                  Mfg: {b.manufacture_date || "—"}<br/>
                  Exp: {b.expiration_date || "—"}
                </div>
                <div className="col-span-2 text-xs font-mono">
                  <span className="text-brand-muted">Received</span> <span className="text-brand-navy">{b.quantity_received}</span><br/>
                  <span className="text-brand-muted">Available</span> <span className="text-brand-accent">{b.quantity_available}</span>
                </div>
                <div className="col-span-2">
                  <Select value={b.status} onValueChange={(v) => transition(b.id, v)}>
                    <SelectTrigger data-testid={`batch-status-${b.id.slice(0,6)}`} className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{BATCH_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="mt-1 text-[10px] font-mono text-brand-muted">COA: {b.coa_status}</div>
                </div>
                <div className="col-span-3 flex flex-col gap-2 items-end text-right">
                  <UploadCOA batch={b} onUploaded={load} />
                  {b.expiration_date && new Date(b.expiration_date) < new Date(Date.now() + 90 * 86400000) && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider border border-amber-500/40 text-amber-300 bg-amber-500/5 px-2 py-0.5">
                      <WarningCircle size={10} weight="bold" /> Expiring soon
                    </span>
                  )}
                  {b.status === "Recalled" && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider border border-red-500/40 text-red-300 bg-red-500/5 px-2 py-0.5">
                      Recalled
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminGuard>
  );
}
